import { useId } from 'react';
import { Box, Text } from '@cowbell/ui';
import { AttachmentChip } from '../../AttachmentChip';
import {
  removeEmptyBlocks,
  restoreFlattenedTables,
  sanitizeEmailHtml,
  selectHtmlBody,
} from '../../../utils/htmlContentUtils';

import type {
  MessageInfo,
  MessageInfoAttachment,
  MessageInfoChild,
} from '../../../../../api/cmail/cmail.dto';
import classes from './MessageBody.module.css';

// Note: Email HTML sanitization is now handled by the sanitizeEmailHtml utility function
// This ensures secure, consistent sanitization across all email components

/**
 * Invoice slices sit under the PDF they were split from. A slice whose parent
 * is not in the list stays at the top level so it is never hidden.
 */
const groupAttachments = (attachments: readonly MessageInfoAttachment[]) => {
  const docIds = new Set(attachments.map((attachment) => attachment.docId));
  const topLevel: MessageInfoAttachment[] = [];
  const slicesByParent = new Map<string, MessageInfoAttachment[]>();
  attachments.forEach((attachment) => {
    const { parentDocId } = attachment;
    if (parentDocId && docIds.has(parentDocId)) {
      slicesByParent.set(parentDocId, [
        ...(slicesByParent.get(parentDocId) ?? []),
        attachment,
      ]);
    } else {
      topLevel.push(attachment);
    }
  });
  return { topLevel, slicesByParent };
};

export const MessageBody = ({
  message,
}: {
  message: MessageInfo | MessageInfoChild;
}) => {
  const htmlBody = selectHtmlBody(message);
  // docmessage keeps slices in their own list so they are never re-extracted.
  const { topLevel, slicesByParent } = groupAttachments([
    ...(message.attachments ?? []),
    ...(message.invoiceSlices ?? []),
  ]);
  // Each message gets its own scope so one email's stylesheet styles neither
  // the console nor the other messages in the thread.
  const scopeId = useId();
  const scope = `[data-email-scope="${scopeId}"]`;

  // Get content - prefer HTML if available, fallback to text
  const getContent = () => {
    if (htmlBody) {
      let content = htmlBody;

      // Remove CID images using string replacement to prevent browser from trying to load them
      content = content.replace(
        /<img[^>]*src\s*=\s*["']cid:[^"']*["'][^>]*>/gi,
        ''
      );

      content = content.replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
      content = removeEmptyBlocks(content);

      // Mail forwarded from the Google Groups web UI arrives with its tables
      // flattened to spans; rebuild them so the layout survives.
      content = restoreFlattenedTables(content);

      // Sanitize HTML to prevent XSS while preserving legitimate email formatting

      return sanitizeEmailHtml(content, scope);
    }
    return { html: message.textBodyAll || '', css: '' };
  };

  const contentResult = getContent();
  const content = contentResult.html;
  const extractedCSS = contentResult.css;

  const isHtmlContent = Boolean(htmlBody) && content.includes('<');

  return (
    <>
      {/*
        Apply sanitized CSS styles that were safely extracted and validated.
        The CSS sanitizer removes all dangerous patterns (XSS, data exfiltration, etc.)
        while preserving legitimate email layout styles (container widths, centering, etc.).
        Every rule is scoped to this message's container.
      */}
      {extractedCSS && (
        <style
          dangerouslySetInnerHTML={{
            __html: extractedCSS,
          }}
        />
      )}

      <Box className={classes.emailBody}>
        {/* The scope is the email's own HTML and nothing else: an email's
            `body` rule must not reach our padding or the attachment chips. */}
        {isHtmlContent ? (
          <div
            data-email-scope={scopeId}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        ) : (
          <Text size="sm" className={classes.plainText}>
            {content}
          </Text>
        )}

        {topLevel.length > 0 && (
          <Box className={classes.attachments}>
            {topLevel.map((attachment) => (
              <AttachmentChip
                key={attachment.docId}
                attachment={attachment}
                slices={slicesByParent.get(attachment.docId)}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};
